import React from 'react'
import styled from 'styled-components'
import GridCard from 'employee-platform/shared/components/GridCard'
import FlexContainer from 'core-system/FlexContainer'
import Text from 'core-system/Text'
import Divider from 'core-system/Divider'
import palette from 'core-system/Themes/palette'
import { BaseProgram } from 'redux/employeePlatform/employeePlatformTypes'
import { formatCurrency } from 'shared/CurrencyFunctions'
import { CurrencyType } from 'features/Programs/Shared/types'
import moment from 'moment'
import {
  getMicromobilityCategories,
  getProgramDisplayName,
  getResetsInLabel,
} from '../utils'

const ProgramSummaryRow = styled(FlexContainer)`
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
`

const SmallPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  background-color: ${palette.grey.grey100};
  color: ${palette.text.secondary};
  font-size: 0.75rem;
  line-height: 1.1;
`

const ProgramInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`

const ProgramDescription = styled(Text)`
  color: ${palette.text.secondary};
`

const ResetRow = styled(FlexContainer)`
  justify-content: space-between;
  align-items: center;
`

const EligibleCategoriesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin-top: 0.125rem;
`

const CategoryItem = styled(FlexContainer)`
  align-items: center;
  gap: 0.625rem;
  padding: 0.25rem 0;
`

const CategoryIcon = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background-color: ${palette.grey.grey100};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${palette.text.secondary};
`

const CategoryInfo = styled(FlexContainer)`
  flex-direction: column;
  gap: 0.0625rem;
  min-width: 0;
`

interface MicromobilityCardProps {
  program: BaseProgram
  currency: CurrencyType
  conversionRates: Dictionary
}

const MicromobilityCard = ({
  program,
  currency,
  conversionRates,
}: MicromobilityCardProps) => {
  const remaining = program.remainingBudget ?? program.budget ?? 0
  const total = program.budget ?? 0
  const categories = getMicromobilityCategories(program)

  return (
    <GridCard
      key={program.id}
      headerTitle={getProgramDisplayName(program)}
      borderColor={palette.secondary.purple400}
    >
      <FlexContainer flexDirection='column' style={{ gap: '0.75rem' }}>
        <ProgramInfoSection>
          <ProgramSummaryRow>
            <FlexContainer
              alignItems='baseline'
              style={{
                gap: '0.375rem',
                rowGap: '0.25rem',
                flexWrap: 'wrap',
              }}
            >
              <Text variant='h2' textColor={palette.secondary.purple600}>
                {formatCurrency(currency, remaining, conversionRates)}
              </Text>
              <Text variant='action2' textColor={palette.text.secondary}>
                / {formatCurrency(currency, total, conversionRates)}
              </Text>
              <SmallPill>{(program.budgetCycle || 'MONTHLY').toLowerCase()} budget</SmallPill>
            </FlexContainer>
          </ProgramSummaryRow>

          <ResetRow>
            <Text variant='action4' textColor={palette.text.secondary}>
              {getResetsInLabel(program.cycleEnd)}
            </Text>
            <Text variant='action4' textColor={palette.text.secondary}>
              {moment(program.cycleEnd).format('MMM D, YYYY')}
            </Text>
          </ResetRow>

          {program.description ? (
            <ProgramDescription variant='action4'>
              {program.description}
            </ProgramDescription>
          ) : null}
        </ProgramInfoSection>

        <Divider margin='0.5rem 0' />

        <EligibleCategoriesSection aria-label='Eligible categories'>
          <Text variant='action4' textColor={palette.text.secondary}>
            Eligible categories
          </Text>
          {categories.map((cat, idx) => (
            <React.Fragment key={cat.key}>
              {idx > 0 ? <Divider margin='0.25rem 0' /> : null}
              <CategoryItem>
                <CategoryIcon>{cat.icon}</CategoryIcon>
                <CategoryInfo>
                  <Text variant='action2' fontWeight={600}>
                    {cat.label}
                  </Text>
                  <Text variant='action4' textColor={palette.text.secondary}>
                    {cat.description}
                  </Text>
                </CategoryInfo>
              </CategoryItem>
            </React.Fragment>
          ))}
        </EligibleCategoriesSection>
      </FlexContainer>
    </GridCard>
  )
}

export default MicromobilityCard
